import React from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, KeyRound, UserCheck } from 'lucide-react';
import SeoMeta from '../components/SeoMeta';
import { useLocalizedUrl } from '../utils/urlHelper';
import { PageHero, SectionIntro, ClosingCta, CheckList, usePrimaryCta } from '../components/common/B2B';
import { Section, ItemsSection, HeroChecks, TwoColumns, RelatedSection } from '../components/common/B2BSections';
import { otherTrustPages, RELATED_TITLE } from '../components/common/trustPages';

// Confianza · Seguridad (nov-2026), rediseño B2B. El bloque del ENS va primero y
// conserva TODO lo que exigen las reglas de OCA CERT (P.G.LOGOS.MARCAS.SG
// Rev.12.21), solo con otro estilo:
//  - el distintivo sin alterar, con su marco, escalado proporcional y formando un
//    solo conjunto gráfico con la razón social del certificado (BLUECMR, S.L.U.);
//  - el n.º de certificado FUERA de la marca y el alcance literal;
//  - el certificado se publica COMPLETO (§4.6.4): el PDF va tal cual lo emitió
//    OCA, firmado digitalmente (recomprimirlo invalidaría la firma). Si se
//    suspende, se reduce el alcance o caduca (24-ago-2028), retirarlo (§4.6.3, §4.7.2).
// Sin la categoría en el texto, como en el resto de la web (criterio de Jesús,
// 25-ago-2026, confirmado el 4-nov para este bloque): solo la lleva impresa el
// distintivo, que no se puede alterar. En inglés, la fórmula textual sin logo (§4.5).
// El resto del contenido es el de la página anterior, condensado.
// Hero con foto (4-nov, Jesús: imágenes en las páginas que no tenían): portátil
// con el inicio de sesión REAL de BlueCMR (captura de private-app montada en la
// pantalla de una foto GPT Image 2, marketing/prompts-imagenes/hero-seguridad-portatil.txt,
// gpt-02). Jesús: «el login genérico queda muy mal, o se pone uno de BlueCMR o ninguno».
const HERO_IMAGE = { src: '/images/confianza/hero-seguridad.webp', position: '70% 50%' };

const PAGE_URL = { es: 'https://www.bluecmr.com/confianza/seguridad', en: 'https://www.bluecmr.com/en/trust/security' };
const ENS_PDF = '/certificado-ens-bluecmr-34-5704-26-08130.pdf';

const COPY = {
  es: {
    seoTitle: 'Seguridad: certificación ENS, cifrado y accesos | BlueCMR',
    seoDescription: 'Cómo protege BlueCMR los documentos de transporte: sistema certificado en el ENS por OCA CERT, datos en la UE, cifrado y doble factor.',
    seoKeywords: 'seguridad documentos de transporte, esquema nacional de seguridad, ens, certificación ens, cifrado, doble factor',
    heroEyebrow: 'Confianza · Seguridad',
    heroTitle: 'Seguridad certificada para sus documentos de transporte',
    heroText: 'BlueCMR protege la información, los accesos y las evidencias de cada documento durante todo su ciclo de vida, con un sistema de seguridad de la información certificado en el Esquema Nacional de Seguridad.',
    heroChecks: ['Certificación ENS', 'Datos en la Unión Europea', 'Doble factor de autenticación'],
    ensMeaning: [
      'Una Política de Seguridad de la Información aprobada por la dirección y revisada periódicamente.',
      'Análisis y gestión de riesgos, control de accesos con MFA, cifrado, registro de actividad y monitorización continua.',
      'Gestión de incidentes, copias de seguridad y continuidad del servicio sobre infraestructura AWS (certificada ENS categoría ALTA).',
      'Mejora continua y auditoría periódica de las medidas de seguridad.',
    ],
    infra: {
      eyebrow: 'Infraestructura',
      title: 'Una arquitectura con poca superficie de ataque',
      cols: 4,
      items: [
        { title: 'AWS, en la UE', text: 'Toda la plataforma funciona sobre Amazon Web Services, con servicios gestionados y auditados, y los datos alojados en la Unión Europea.' },
        { title: 'Sin servidores expuestos', text: 'La lógica se ejecuta en AWS Lambda: entornos efímeros que se destruyen tras cada uso, cada función con permisos limitados.' },
        { title: 'Entornos aislados', text: 'Pruebas y producción separados, y aislamiento entre datos, lógica y credenciales.' },
        { title: 'Registro de actividad', text: 'Accesos y acciones relevantes registrados, eventos de seguridad analizados e incidentes investigados de forma controlada.' },
      ],
    },
    protEyebrow: 'Protección',
    protTitle: 'Claves y accesos bajo control',
    prot: [
      { icon: KeyRound, title: 'Cifrado y claves', text: 'Los datos van cifrados en tránsito y en reposo.', items: ['Claves gestionadas en AWS Key Management Service (KMS)', 'Nunca accesibles directamente para los usuarios', 'Uso de las claves controlado, rotado y auditado', 'Biometría de las firmas cifrada en el propio dispositivo'] },
      { icon: UserCheck, title: 'Accesos y autenticación', text: 'Cada persona ve solo lo que le corresponde.', items: ['Autenticación de usuarios con AWS Cognito', 'Doble factor con Google o Microsoft Authenticator', 'Inicio de sesión único con Microsoft Entra ID', 'Permisos mínimos por usuario, función y centro', 'Sesiones con caducidad automática'] },
    ],
    shareEyebrow: 'Responsabilidad compartida',
    shareTitle: 'Qué protege BlueCMR y qué protege cada cliente',
    share: [
      { title: 'BlueCMR', items: ['Proteger la infraestructura y la plataforma', 'Aplicar los controles técnicos y organizativos', 'Garantizar la integridad y la disponibilidad del servicio'] },
      { title: 'Cada cliente', items: ['Proteger sus credenciales', 'Usar la autenticación reforzada', 'Gestionar los accesos dentro de su organización'] },
    ],
    shareNote: 'Las entidades cliente, proveedores y demás partes interesadas pueden pedir más información sobre nuestro marco de seguridad en contact@bluecmr.com. La documentación interna detallada (políticas, normativa y procedimientos) se facilita bajo los acuerdos de confidencialidad correspondientes.',
    closing: 'Resolvemos las preguntas de su equipo de seguridad',
    closingText: 'Le enseñamos la plataforma y le facilitamos la documentación que necesite, sin compromiso.',
    breadcrumb: ['Inicio', 'Confianza', 'Seguridad'],
  },
  en: {
    seoTitle: 'Security: ENS certification, encryption and access | BlueCMR',
    seoDescription: 'How BlueCMR protects transport documents: a management system certified under Spain’s ENS by OCA CERT, data in the EU, encryption and two-factor login.',
    seoKeywords: 'transport document security, ens certification, national security framework spain, encryption, two-factor authentication',
    heroEyebrow: 'Trust · Security',
    heroTitle: 'Certified security for your transport documents',
    heroText: 'BlueCMR protects the information, access and evidence of every document throughout its lifecycle, with an information security system certified under Spain\'s National Security Framework (ENS).',
    heroChecks: ['ENS certification', 'Data in the European Union', 'Two-factor authentication'],
    ensTitle: 'National Security Framework (ENS) certification',
    ensText: 'BLUECMR, S.L.U. holds a management system certified under Royal Decree 311/2022 (ENS) by OCA CERT.',
    ensLink: 'View the full certificate (PDF, Spanish and English, 3.4 MB)',
    infra: {
      eyebrow: 'Infrastructure',
      title: 'An architecture with a small attack surface',
      cols: 4,
      items: [
        { title: 'AWS, in the EU', text: 'The whole platform runs on Amazon Web Services, with managed and audited services, and data hosted in the European Union.' },
        { title: 'No exposed servers', text: 'Logic runs on AWS Lambda: ephemeral environments destroyed after each use, each function with limited permissions.' },
        { title: 'Isolated environments', text: 'Separate test and production, and isolation between data, logic and credentials.' },
        { title: 'Activity logging', text: 'Relevant access and actions are logged, security events analysed and incidents investigated in a controlled way.' },
      ],
    },
    protEyebrow: 'Protection',
    protTitle: 'Keys and access under control',
    prot: [
      { icon: KeyRound, title: 'Encryption and keys', text: 'Data is encrypted in transit and at rest.', items: ['Keys managed in AWS Key Management Service (KMS)', 'Never directly accessible to users', 'Key use controlled, rotated and audited', 'Signature biometrics encrypted on the device itself'] },
      { icon: UserCheck, title: 'Access and authentication', text: 'Each person sees only what they should.', items: ['User authentication with AWS Cognito', 'Two-factor with Google or Microsoft Authenticator', 'Single sign-on with Microsoft Entra ID', 'Least-privilege permissions per user, role and site', 'Sessions that expire automatically'] },
    ],
    shareEyebrow: 'Shared responsibility',
    shareTitle: 'What BlueCMR protects and what each customer protects',
    share: [
      { title: 'BlueCMR', items: ['Protect the infrastructure and the platform', 'Apply technical and organisational controls', 'Guarantee the integrity and availability of the service'] },
      { title: 'Each customer', items: ['Protect their credentials', 'Use strong authentication', 'Manage access within their organisation'] },
    ],
    shareNote: 'Customers, suppliers and other stakeholders can request more information about our security framework at contact@bluecmr.com. Detailed internal documentation (policies, rules and procedures) is provided under the corresponding confidentiality agreements.',
    closing: 'We answer your security team\'s questions',
    closingText: 'We will show you the platform and provide the documentation you need, with no commitment.',
    breadcrumb: ['Home', 'Trust', 'Security'],
  },
};

/** Bloque formal del ENS (solo en español), con las reglas de uso del distintivo de OCA CERT. */
const EnsCertification = ({ meaning }) => (
  <Section tone="white">
    <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
      <div className="lg:col-span-4">
        <SectionIntro eyebrow="Esquema Nacional de Seguridad" title="Certificación del ENS" className="mb-8" />
        {/* Conjunto gráfico: marca de certificación + razón social certificada */}
        <figure className="inline-block text-center">
          <img
            src="/distintivo-ens-media-rd311-2022.png"
            alt="Distintivo de certificación de conformidad con el Esquema Nacional de Seguridad, Real Decreto 311/2022"
            width="925"
            height="1125"
            className="w-40 h-auto mx-auto"
          />
          <figcaption className="mt-2 text-sm font-bold text-slate-900">BLUECMR, S.L.U.</figcaption>
        </figure>
      </div>
      <div className="lg:col-span-8">
        <p className="text-lg text-slate-700 leading-relaxed">
          <strong>BLUECMR, S.L.U.</strong> está <strong>certificada en el Esquema Nacional de Seguridad (ENS)</strong>,
          regulado por el <strong>Real Decreto 311/2022</strong>, tras la
          auditoría de conformidad realizada por <strong>OCA Instituto de Certificación, S.L.U.</strong>, entidad
          acreditada por ENAC.
        </p>
        <p className="mt-4 text-slate-600 leading-relaxed">
          El alcance certificado es <em>«el sistema de seguridad de la información que da soporte a los servicios de
          comercialización de software SaaS de documentos de transporte digitales»</em>, con las cinco dimensiones
          —confidencialidad, integridad, trazabilidad, autenticidad y disponibilidad— y 66 medidas de
          seguridad aplicadas.
        </p>
        <dl className="mt-8 grid sm:grid-cols-2 gap-x-10 border-t border-slate-300 text-[15px]">
          {[
            ['N.º de certificado', '34/5704/26/08130'],
            ['Entidad certificadora', 'OCA Instituto de Certificación, S.L.U.'],
            ['Fecha de concesión', '24 de agosto de 2026'],
            ['Válido hasta', '24 de agosto de 2028'],
          ].map(([k, v]) => (
            <div key={k} className="flex justify-between gap-4 border-b border-slate-200 py-3">
              <dt className="text-slate-500">{k}</dt>
              <dd className="font-semibold text-slate-900 text-right">{v}</dd>
            </div>
          ))}
        </dl>
        <a href={ENS_PDF} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:underline">
          <FileText className="w-4 h-4" /> Ver el certificado completo (PDF, 3,4 MB)
        </a>
        <h3 className="mt-10 font-semibold text-slate-900">Qué significa para nuestros clientes y partes interesadas</h3>
        <CheckList items={meaning} className="mt-4 space-y-2.5" />
      </div>
    </div>
  </Section>
);

const SeguridadPage = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'es' ? 'es' : 'en';
  const c = COPY[lang];
  const { getLocalizedUrl } = useLocalizedUrl();
  const primaryCta = usePrimaryCta(lang);

  return (
    <>
      <SeoMeta
        title={c.seoTitle}
        description={c.seoDescription}
        canonical={PAGE_URL[lang]}
        type="website"
        alternates={{ es: PAGE_URL.es, en: PAGE_URL.en, 'x-default': PAGE_URL.es }}
        keywords={c.seoKeywords}
        breadcrumbs={[
          { name: c.breadcrumb[0], url: lang === 'es' ? 'https://www.bluecmr.com/' : 'https://www.bluecmr.com/en' },
          { name: c.breadcrumb[2], url: PAGE_URL[lang] },
        ]}
      />

      <div className="bg-white">
        <PageHero eyebrow={c.heroEyebrow} title={c.heroTitle} text={c.heroText} image={HERO_IMAGE} primary={primaryCta} compact>
          <HeroChecks items={c.heroChecks} />
        </PageHero>

        {lang === 'es' ? (
          <EnsCertification meaning={c.ensMeaning} />
        ) : (
          <Section tone="white">
            <SectionIntro eyebrow="ENS" title={c.ensTitle} text={c.ensText} eyebrowAsIs className="max-w-3xl mb-6" />
            <a href={ENS_PDF} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:underline">
              <FileText className="w-4 h-4" /> {c.ensLink}
            </a>
          </Section>
        )}

        <ItemsSection {...c.infra} tone="gray" />

        <Section tone="white">
          <SectionIntro eyebrow={c.protEyebrow} title={c.protTitle} />
          <TwoColumns cols={c.prot} />
        </Section>

        <Section tone="gray">
          <SectionIntro eyebrow={c.shareEyebrow} title={c.shareTitle} />
          <TwoColumns cols={c.share} />
          <p className="mt-12 max-w-3xl text-[15px] text-slate-600 leading-relaxed">{c.shareNote}</p>
        </Section>

        <RelatedSection title={RELATED_TITLE[lang]} items={otherTrustPages(lang, 'seguridad', getLocalizedUrl)} />

        <ClosingCta title={c.closing} text={c.closingText} lang={lang} />
      </div>
    </>
  );
};

export default SeguridadPage;
